import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { cn } from '../../lib/utils';

export interface SignaturePadHandle {
  /** Efface le trait et repasse le canvas au fond uni. */
  clear: () => void;
  /** `true` tant qu'aucun trait n'a été dessiné depuis le montage / le dernier `clear()`. */
  isEmpty: () => boolean;
  /**
   * Exporte le contenu du canvas en data URL (PNG par défaut).
   * Retourne `null` si le canvas est vide — évite d'envoyer un PNG blanc au backend.
   */
  toDataURL: (type?: string, quality?: number) => string | null;
}

export interface SignaturePadProps {
  /** Largeur du canvas en pixels CSS (résolution interne alignée dessus). Défaut 400. */
  width?: number;
  /** Hauteur du canvas en pixels CSS. Défaut 150. */
  height?: number;
  /** Couleur du trait. Défaut bleu foncé. */
  penColor?: string;
  /** Épaisseur du trait en pixels. Défaut 3. */
  penWidth?: number;
  /** Couleur de fond du canvas. Défaut blanc. */
  backgroundColor?: string;
  className?: string;
  /** Appelé à chaque transition vide <-> non-vide (pas à chaque trait). */
  onChange?: (hasSignature: boolean) => void;
  /** Désactive le dessin (ex : pendant une soumission en cours). Défaut false. */
  disabled?: boolean;
  'aria-label'?: string;
}

/**
 * Zone de signature tactile générique (canvas HTML5 + Pointer Events —
 * fonctionne indifféremment à la souris, au doigt ou au stylet, sans lib
 * externe). Composant purement présentationnel : aucune logique métier,
 * aucun appel réseau. Le consommateur pilote l'export via la ref
 * (`toDataURL()` / `clear()` / `isEmpty()`).
 *
 * Design volontairement simple (un seul trait de couleur fixe, pas
 * d'undo/redo) — étendre si un futur usage le demande.
 */
export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(
  function SignaturePad(
    {
      width = 400,
      height = 150,
      penColor = '#1E3A8A',
      penWidth = 3,
      backgroundColor = '#ffffff',
      className,
      onChange,
      disabled = false,
      'aria-label': ariaLabel = 'Zone de signature',
    },
    ref
  ) {
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const drawingRef = useRef(false);
    const hasSignatureRef = useRef(false);
    const lastPointRef = useRef<{ x: number; y: number } | null>(null);

    // Si `disabled` bascule à true en cours de trait, on arrête proprement le
    // dessin en cours (sinon un pointerup après coup pourrait encore
    // continuer le trait avant que React ne re-render).
    useEffect(() => {
      if (disabled) {
        drawingRef.current = false;
        lastPointRef.current = null;
      }
    }, [disabled]);

    const paintBackground = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx) return;
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    };

    useEffect(() => {
      paintBackground();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [width, height, backgroundColor]);

    const getPoint = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current!;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
    };

    const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      e.preventDefault();
      canvasRef.current?.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      lastPointRef.current = getPoint(e);
    };

    const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (disabled || !drawingRef.current) return;
      e.preventDefault();
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!canvas || !ctx || !lastPointRef.current) return;

      const point = getPoint(e);
      ctx.strokeStyle = penColor;
      ctx.lineWidth = penWidth;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      lastPointRef.current = point;

      if (!hasSignatureRef.current) {
        hasSignatureRef.current = true;
        onChange?.(true);
      }
    };

    const stopDrawing = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      drawingRef.current = false;
      lastPointRef.current = null;
      if (canvasRef.current?.hasPointerCapture(e.pointerId)) {
        canvasRef.current.releasePointerCapture(e.pointerId);
      }
    };

    useImperativeHandle(
      ref,
      () => ({
        clear: () => {
          paintBackground();
          hasSignatureRef.current = false;
          onChange?.(false);
        },
        isEmpty: () => !hasSignatureRef.current,
        toDataURL: (type = 'image/png', quality) => {
          if (!hasSignatureRef.current) return null;
          return canvasRef.current?.toDataURL(type, quality) ?? null;
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    );

    return (
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        role="img"
        aria-label={ariaLabel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDrawing}
        onPointerLeave={stopDrawing}
        onPointerCancel={stopDrawing}
        className={cn(
          'touch-none rounded-xl border-2 border-neutral-300',
          disabled && 'pointer-events-none opacity-60',
          className
        )}
      />
    );
  }
);
